import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Table } from 'reactstrap';
import FormCard from '../../../shared/components/custom/FormCard';
import Loading from '../../../shared/components/custom/Loading';
import Api from '../../../util/api';
import { formatDate } from '../../../util/date-and-time';
import { partial } from '../../../util/translation/translation';

const p = partial('chat');

// Open reports from the app, one row per reported message: keep it (closes
// the reports) or delete it.
const ChatReportsCard = ({ isBlocked, onBlockUser }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      setReports(await Api.chat.getReports());
    } catch (err) {
      toast.error(err || p('reportsRetrievingERROR'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const byMessage = useMemo(() => {
    const groups = new Map();
    reports.forEach((report) => {
      const group = groups.get(report.messageId) || {
        ...report,
        count: 0,
        reasons: [],
      };
      group.count += 1;
      if (report.reason) group.reasons.push(report.reason);
      groups.set(report.messageId, group);
    });
    return [...groups.values()];
  }, [reports]);

  const resolve = async (reportId, action) => {
    setLoading(true);
    try {
      await Api.chat.resolveReport(reportId, action);
      toast.success(
        action === 'delete' ? p('reportMessageDeleted') : p('reportDismissed')
      );
    } catch (err) {
      toast.error(err || p('reportResolveERROR'));
    } finally {
      fetchReports();
    }
  };

  return (
    <FormCard
      title={p('reportedMessages')}
      bodyClassName="form-card__body--flush"
      className="mb-3"
    >
      <Loading loading={loading} />
      <Table responsive striped className="table--roomy table--stack">
        <thead>
          <tr>
            <th>{p('reportedAt')}</th>
            <th>{p('chat')}</th>
            <th>{p('message')}</th>
            <th>{p('reasons')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {byMessage.length === 0 && !loading ? (
            <tr>
              <td colSpan={5} className="text-muted">
                {p('noReports')}
              </td>
            </tr>
          ) : (
            byMessage.map((report) => (
              <tr key={report.messageId}>
                <td className="table-stack__primary">
                  {formatDate(report.createdAt)}
                </td>
                <td data-label={p('chat')}>
                  {report.chatId ? (
                    <Link to={`/chats/edit/${report.chatId}`}>
                      {report.chatName || '-'}
                    </Link>
                  ) : (
                    '-'
                  )}
                </td>
                <td
                  data-label={p('message')}
                  style={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}
                >
                  <strong>{report.message?.name || '-'}</strong>
                  {report.message?.hidden && !report.message?.deleted && (
                    <Badge color="warning" className="ml-2">
                      {p('messageHiddenBadge')}
                    </Badge>
                  )}
                  {report.message?.deleted && (
                    <Badge color="secondary" className="ml-2">
                      {p('messageDeletedBadge')}
                    </Badge>
                  )}
                  <div>{report.message?.text || ''}</div>
                </td>
                <td data-label={p('reasons')}>
                  <Badge color="danger" className="mr-2">
                    {p('reportCount', { count: report.count })}
                  </Badge>
                  {report.reasons.join(' · ')}
                </td>
                <td className="text-nowrap table-stack__actions">
                  <Button
                    size="sm"
                    color="secondary"
                    className="mb-0 mr-2"
                    onClick={() => resolve(report.id, 'dismiss')}
                  >
                    {p('keepMessage')}
                  </Button>
                  <Button
                    size="sm"
                    color="danger"
                    className="mb-0 mr-2"
                    disabled={report.message?.deleted}
                    onClick={() => resolve(report.id, 'delete')}
                  >
                    {p('deleteMessage')}
                  </Button>
                  {report.message?.userId && (
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      className="mb-0"
                      disabled={isBlocked(report.message.userId)}
                      onClick={() =>
                        onBlockUser(report.message.userId, report.message.name)
                      }
                    >
                      {isBlocked(report.message.userId)
                        ? p('userIsBlocked')
                        : p('blockUser')}
                    </Button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </FormCard>
  );
};

export default ChatReportsCard;
